import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { contactAPI } from '../services/api';
import PageHeader from './PageHeader';
import './ContactRelatedClients.css';

function ContactRelatedClients() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [contact, setContact] = useState(null);
  const [relatedClients, setRelatedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await contactAPI.getRelatedClients(id);
        const { data } = response.data;
        if (!cancelled) {
          setContact(data.contact);
          setRelatedClients(data.relatedClients || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading related clients:', err);
          setError(err.response?.status === 404 ? 'Contact not found' : 'Failed to load related clients');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const contactName = contact
    ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Contact'
    : '';

  if (loading) {
    return (
      <div className="contact-related-clients">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-related-clients">
        <PageHeader title="Related clients" />
        <div className="related-clients-error">{error}</div>
        <button type="button" className="btn-back" onClick={() => navigate(tenantPath('/lists/contacts'))}>
          Back to Contacts
        </button>
      </div>
    );
  }

  return (
    <div className="contact-related-clients">
      <PageHeader title={`Clients related to ${contactName}`}>
        <button type="button" className="btn-back" onClick={() => navigate(tenantPath('/lists/contacts'))}>
          Back to Contacts
        </button>
      </PageHeader>

      {relatedClients.length === 0 ? (
        <p className="related-clients-empty">This contact is not linked as primary or secondary contact for any client.</p>
      ) : (
        <div className="related-clients-table-wrap">
          <table className="related-clients-table">
            <thead>
              <tr>
                <th>Client name</th>
                <th>EI number</th>
                <th>Relationship</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {relatedClients.map((row) => (
                <tr key={`${row.client_id}-${row.relationship_type}`}>
                  <td>{row.last_name}, {row.first_name}</td>
                  <td>{row.ei_number ?? '—'}</td>
                  <td>{row.relationship_description ?? '—'}</td>
                  <td>
                    <Link to={tenantPath(`/client/${row.client_id}`)} className="link-nav">
                      View client
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ContactRelatedClients;
